import { useState } from 'react';
import { moneyShort } from '../../lib/format';
import { usd } from './shared';

export interface ChartPoint {
  key: string;
  label: string;
  income: number;
  expense: number;
  net: number;
  months: number;
  complete: boolean;
}

const W = 720;
const H = 300;
const PAD = { top: 16, right: 16, bottom: 34, left: 56 };

/** Grouped bars of income against expenses. Incomplete periods are drawn lighter. */
export function IncomeExpenseChart({ points, granularity }: { points: ChartPoint[]; granularity: string }) {
  const [active, setActive] = useState<number | null>(null);
  if (points.length === 0) return null;

  const max = Math.max(...points.map((p) => Math.max(p.income, p.expense)), 1) * 1.1;
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const slot = plotW / points.length;
  const barW = Math.min(34, slot * 0.34);
  const y = (v: number) => PAD.top + plotH - (Math.max(v, 0) / max) * plotH;
  const grid = [0.25, 0.5, 0.75, 1].map((f) => f * max);
  const hovered = active === null ? null : points[active];

  return (
    <div style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="group"
        aria-label={`Income against expenses by ${granularity} period. ${points
          .map((p) => `${p.label}: income ${usd(p.income)}, expenses ${usd(p.expense)}`)
          .join('; ')}.`}
        style={{ display: 'block', overflow: 'visible' }}
        onMouseLeave={() => setActive(null)}
      >
        {grid.map((v) => (
          <g key={v}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} stroke="var(--bd)" />
            <text x={PAD.left - 8} y={y(v) + 4} fontSize="10" textAnchor="end" fill="var(--mut)">
              {moneyShort(v)}
            </text>
          </g>
        ))}
        <line x1={PAD.left} x2={W - PAD.right} y1={y(0)} y2={y(0)} stroke="var(--mid)" />

        {points.map((p, i) => {
          const cx = PAD.left + slot * i + slot / 2;
          const opacity = p.complete ? 1 : 0.45;
          return (
            <g
              key={p.key}
              onMouseEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              onBlur={() => setActive(null)}
              tabIndex={0}
              aria-label={`${p.label}: income ${usd(p.income)}, expenses ${usd(p.expense)}, net ${usd(p.net)}${p.complete ? '' : ', partial period'}`}
            >
              <rect x={PAD.left + slot * i} y={PAD.top} width={slot} height={plotH} fill="transparent" />
              <rect x={cx - barW - 2} y={y(p.income)} width={barW} height={Math.max(0, y(0) - y(p.income))} rx="4" fill="var(--ok)" opacity={opacity} />
              <rect x={cx + 2} y={y(p.expense)} width={barW} height={Math.max(0, y(0) - y(p.expense))} rx="4" fill="var(--bad)" opacity={opacity} />
              <text x={cx} y={H - 12} fontSize="11" textAnchor="middle" fill="var(--mut)">
                {p.label}
              </text>
            </g>
          );
        })}
      </svg>

      <div className="row" style={{ gap: 16, marginTop: 6 }} aria-hidden="true">
        <span className="mut"><span style={{ color: 'var(--ok)' }}>■</span> Income</span>
        <span className="mut"><span style={{ color: 'var(--bad)' }}>■</span> Expenses</span>
        {points.some((p) => !p.complete) && <span className="mut">Lighter bars cover a partial period</span>}
      </div>

      {hovered && (
        <div
          className="card tight"
          role="status"
          style={{ position: 'absolute', top: 4, right: 4, minWidth: 190, boxShadow: 'var(--sh)' }}
        >
          <strong>{hovered.label}</strong>
          {!hovered.complete && <div className="mut">Partial period · {hovered.months} month{hovered.months === 1 ? '' : 's'}</div>}
          <div className="row between"><span className="mut">Income</span><span>{usd(hovered.income)}</span></div>
          <div className="row between"><span className="mut">Expenses</span><span>{usd(hovered.expense)}</span></div>
          <div className="row between"><span className="mut">Net</span><span className={hovered.net < 0 ? 'bad' : 'ok'}>{usd(hovered.net)}</span></div>
        </div>
      )}
    </div>
  );
}
